import {
	faArrowUpFromBracket,
	faEllipsisVertical,
} from '@fortawesome/pro-regular-svg-icons';
import { DevTool } from '@hookform/devtools';
import { zodResolver } from '@hookform/resolvers/zod';
import { useParams, useRouter, useRouterState } from '@tanstack/react-router';
import {
	ConditionType,
	CurrencyType,
	type FileRequest,
	getGetDocumentsOfShipmentQueryKey,
	getGetEventsByShipmentNumberQueryKey,
	type QuoteDTO,
	type ShipmentListDTO,
	type ShipmentLocationActionDto,
	useAddDocumentToShipment,
	useFetchOrganizationById,
	useGetDocumentsOfShipment,
	useGetInvoiceLinesForPartyByShipment,
	WeightUnit,
} from '@uturn/api/v1';
import {
	type ReviewResponse,
	type UpdateShipmentV2,
	useFetchReview,
	useGetPendingChangeSet,
	useUpdateShipment,
	useUpdateStatus,
} from '@uturn/api/v2';
import {
	type Carrier,
	ShipmentDtoTsoStatus as OrderStatus,
	type ShipmentDto,
	ShipmentDtoShipmentStatus as ShipmentStatus,
	useGetShipment,
} from '@uturn/api/v3';
import { Permission } from '@uturn/portal/abac';
import InsuredLimitThresholdBanner from '@uturn/portal/components/insured-limit-threshold-banner';
import { Page } from '@uturn/portal/components/page';
import { MetaDataContext } from '@uturn/portal/context';
import {
	CancelOrUnpublishShipmentModal,
	CarrierReviewBanner,
	ChangeRequestBanner,
	ChangeRequestModal,
	CopyShipmentTypeModal,
	CostDeclaredBanner,
	PublishDetailsSection,
	QuotesSheet,
	RouteSection,
	ShipmentDetailsSection,
	ShipmentMapSection,
} from '@uturn/portal/modules/shipments/components';
import {
	EventsShipmentHistorySheet,
	ShareShipmentSheet,
} from '@uturn/portal/modules/shipments/components/shipment-sheet';
import { useDetermineChangeRequest } from '@uturn/portal/modules/shipments/hooks';
import { ShipmentDetailContext } from '@uturn/portal/modules/shipments/providers/shipment-detail';
import type { FormValues } from '@uturn/portal/modules/shipments/schema';
import { formSchema } from '@uturn/portal/modules/shipments/schema';
import type { CopyOptions } from '@uturn/portal/modules/shipments/utils';
import {
	copyLocations,
	copyRequirements,
	flattenObject,
	fromApi,
	fromForm,
	fromZodLocations,
	getPickupDateUntilFromCopiedLocations,
	getReadOnlyFieldsList,
	getShipmentsNavigation,
	grossWeightUndefined,
	isFieldReadOnly,
	isShipmentArchived,
	isShipmentCancellable,
	isShipmentMatched,
	shouldUpdate,
} from '@uturn/portal/modules/shipments/utils';
import {
	useContainerNumberValidation,
	useGrossWeightValidation,
	usePriceValidation,
	useShipperReferenceValidation,
} from '@uturn/portal/modules/shipments/validation';
import { useLocationDatesValidation } from '@uturn/portal/modules/shipments/validation/location-dates';
import { queryClient } from '@uturn/portal/query';
import { useAddShipmentStore } from '@uturn/portal/store/shipments/add-shipment';
import type { CarrierReview } from '@uturn/portal/types/shipment';
import { ContainerStatus, TypeOfShipment } from '@uturn/portal/types/shipment';
import { formatPrice, getApiErrorMessage } from '@uturn/portal/utils';
import { scrollToFirstFormError } from '@uturn/portal/utils/scroll-to-first-form-error';
import {
	Button,
	cn,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	Form,
	Icon,
	type IconProps,
	sonner,
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@uturn/ui-kit';
import { useContext, useEffect, useRef, useState } from 'react';
import { useAbac } from 'react-abac';
import { Helmet } from 'react-helmet-async';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ConfirmQuotedShipmentUpdateModal } from '../../components/confirm-quoted-shipment-update-modal';
import { CreditLimitPublishBlockedModal } from '../../components/credit-limit-publish-blocked-modal';
import { ShipmentDetailSkeletonLoader } from '../../components/shipment-details-skeleton';
import SummarySection from '../../components/summary-section';
import { ShipmentsNavigationButtons } from './navigation';
import { SideBarActions } from './sidebar-actions';

type ButtonAction = {
	label: string;
	handleOnClick: () => void;
	icon?: IconProps['icon'];
	tooltip?: string;
};

// Quote-impact fields for QUOTED shipments
const QUOTE_IMPACT_KEYS = [
	'containerType',
	'equipment.isoType',
	'equipment.shipmentUnitId',
	'generatorSet',
	'shippingLineId',
	'grossWeight.quantity',
	'grossWeight.unit',
	'unCode',
	'requirementIds',
];

const QUOTE_EDITABLE_READONLY_PATHS = [
	// section-level
	'routeSection',

	// details
	'containerType',
	'equipment.isoType',
	'equipment.shipmentUnitId',
	'generatorSet',
	'shippingLineId',
	'unCode',
	'requirementIds',

	// gross weight
	'grossWeight.quantity',
	'grossWeight.unit',

	// locations
	'locationActions.location',
	'locationActions.dateFrom',
	'locationActions.dateUntil',
];

const QUOTE_IMPACT_REGEX = [
	/^locations\.\d+\.action\.location$/,
	/^locations\.\d+\.action\.location\.name$/,
	/^locations\.\d+\.action\.dateFrom$/,
	/^locations\.\d+\.action\.dateUntil$/,
];

const isQuoteEditablePath = (k: string) =>
	QUOTE_EDITABLE_READONLY_PATHS.includes(k);

function hasQuoteImpactChanges(dirtyFields: object) {
	const flat = flattenObject(dirtyFields);
	return Object.keys(flat).some(
		(key) =>
			QUOTE_IMPACT_KEYS.includes(key) ||
			QUOTE_IMPACT_REGEX.some((re) => re.test(key)),
	);
}

type PendingUpdate = {
	data: UpdateShipmentV2;
	publishing: boolean;
} | null;

const defaultFormValues: Partial<FormValues> = {
	containerType: '',
	publishUntilDate: '',
	equipment: {
		containerNumber: '',
		isoType: '',
		shipmentUnitId: 0,
	},
	grossWeight: {
		quantity: 0,
		unit: WeightUnit.kilogram,
	},
	price: {
		quantity: 0,
		currencyCode: CurrencyType.EUR,
	},
	unCode: '',
	dutiesPaid: undefined,
	generatorSet: false,
	fixedPrice: true,
	conditions: undefined,
	shipperReference: '',
	preferredCarrierIds: [],
	numberOfCopiesToBeCreated: 1,
	containerStatus: undefined, // ''
	seal: '',
	details: '',
	locations: [],
};

export function ShipmentDetail() {
	const stickyRef = useRef<HTMLDivElement>(null);
	const router = useRouter();
	const { determineChangeRequest } = useDetermineChangeRequest();
	const { validateLocationDates } = useLocationDatesValidation();
	const { validateGrossWeight } = useGrossWeightValidation();
	const { validateShipperReference } = useShipperReferenceValidation();
	const { validatePrice } = usePriceValidation();
	const { validateContainerNumber, validateContainerNumberDuplicate } =
		useContainerNumberValidation();

	const { t } = useTranslation();
	const { userHasPermissions } = useAbac();
	const {
		orgDefaultValues,
		setOrgDefaultValues,
		setCopyData,
		setCurrencyCode,
	} = useAddShipmentStore();
	const { metadata, shipmentMetaData, shippingRequirementExceptions } =
		useContext(MetaDataContext);

	const orgId = metadata?.user?.organisation?.id ?? 0;
	const { data: organization } = useFetchOrganizationById(orgId, {
		query: {
			enabled: orgId > 0,
			select: (data) => data.data,
		},
	});

	const [shipmentStatus, setShipmentStatus] = useState<ShipmentStatus>();
	const [shipmentTsoStatus, setShipmentTsoStatus] = useState<
		OrderStatus | undefined
	>(undefined);
	const [isGBRShipment, setIsGBRShipment] = useState(false);
	const [isCrossBorderShipment, setIsCrossBorderShipment] = useState(false);
	const [carrierReview, setCarrierReview] = useState<CarrierReview>(undefined);

	const [pendingChangeRequestActive, setPendingChangeRequestActive] =
		useState(false);
	const [pendingExtraCost, setPendingExtraCost] = useState(false);
	const [pendingCarrierReview, setPendingCarrierReview] = useState(false);

	// const [newPlotLocations, setNewPlotLocations] = useState<any[]>([]);
	// const [newLocationActions, setNewLocationActions] = useState<any[]>([]);
	const [oldLocationActions, setOldLocationActions] = useState<
		ShipmentLocationActionDto[]
	>([]);

	const [assignedCarrier, setAssignedCarrier] = useState<Carrier>();

	const nextShipment = useRef<ShipmentListDTO | undefined>(undefined);
	const prevShipment = useRef<ShipmentListDTO | undefined>(undefined);
	const [enableShipmentNavigation, setEnableShipmentNavigation] =
		useState<boolean>(false);

	const [initData, setInitData] = useState<Partial<FormValues>>({});
	const [wasConcept, setWasConcept] = useState(false);

	const [primaryActions, setPrimaryActions] = useState<ButtonAction[]>([]);
	const [secondaryActions, setSecondaryActions] = useState<ButtonAction[]>([]);
	const [quotes, setQuotes] = useState<QuoteDTO[]>();

	const [openChangeRequestModal, setOpenChangeRequestModal] = useState(false);
	const [openCancelShipmentModal, setOpenCancelShipmentModal] = useState(false);
	const promptChangeRequestModal = useRef<boolean>(true);
	const [
		pendingUpdateDeletingQuotesToConfirm,
		setPendingUpdateDeletingQuotesToConfirm,
	] = useState<PendingUpdate>(null);

	const [
		creditLimitPublishBlockedModalOpen,
		setCreditLimitPublishBlockedModalOpen,
	] = useState(false);

	const {
		readOnlyFields,
		setReadOnlyFields,
		setChangeRequestLocations,
		setChangeRequestChanges,
	} = useContext(ShipmentDetailContext);

	const { shipmentNumber: id } = useParams({
		from: '/_protected/shipments/$shipmentNumber',
	});
	const routerState = useRouterState();
	const { parentQueryKey, parentLocation } = routerState.location.state;

	const isConcept = shipmentStatus === ShipmentStatus.CONCEPT;

	const {
		data: shipment,
		queryKey: shipmentQueryKey,
		isPending: isShipmentPending,
		isError: isShipmentError,
		error: shipmentError,
	} = useGetShipment(id, {
		query: {
			refetchOnWindowFocus: false,
			// gcTime: Infinity, // TODO: Test this new option.
		},
	});

	const [openShareSheet, setOpenShareSheet] = useState(false);
	const [openEventSheet, setOpenEventSheet] = useState(false);
	const [openQuoteSheet, setOpenQuoteSheet] = useState(false);
	const [openCopyTypeModal, setOpenCopyTypeModal] = useState(false);
	// Only cancelled shipments expose the republish action;
	// other status opens the same choice modal for copy.
	const isRepublishFlow = shipmentStatus === ShipmentStatus.CANCELLED;

	const form = useForm<Partial<FormValues>>({
		resolver: zodResolver(formSchema),
		defaultValues: defaultFormValues,
		mode: 'onBlur',
	});

	const {
		queryKey: pendingChangeSetQueryKey,
		data: pendingChangeSet,
		refetch: refetchPendingChangeSet,
		isSuccess: isPendingChangeSetSuccess,
		isError: isPendingChangeSetError,
		error: pendingChangeSetError,
	} = useGetPendingChangeSet(id, {
		query: {
			enabled: false,
			refetchOnWindowFocus: false,
			select: (data) => data.data,
		},
	});

	const {
		data: documents,
		queryKey: documentQueryKey,
		isPending: isDocsPending,
		isError: isDocsError,
		error: docsError,
	} = useGetDocumentsOfShipment(id, {
		query: {
			select: (data) => data.data,
		},
	});

	const handleCarrierReviewUpdate = (reviewResponse?: ReviewResponse) => {
		if (reviewResponse) {
			setCarrierReview(reviewResponse);
			setPendingCarrierReview(false);
			return;
		}
		setCarrierReview(undefined);
		setPendingCarrierReview(isShipmentArchived(shipmentStatus));
	};

	const {
		data: review,
		isSuccess: isReviewSuccess,
		isError: isReviewError,
		error: reviewError,
	} = useFetchReview(id, {
		query: {
			enabled: !!assignedCarrier,
			refetchOnWindowFocus: false,
			select: (data) => data.data,
		},
	});

	const publishingRef = useRef(false);

	const { mutate: updateShipment, isPending: updatingShipment } =
		useUpdateShipment({
			mutation: {
				onSuccess: () => {
					if (publishingRef.current) {
						setPublishStatus.mutate({ shipmentId: id });
					} else {
						queryClient.invalidateQueries({ queryKey: shipmentQueryKey });
						queryClient.invalidateQueries({
							queryKey: pendingChangeSetQueryKey,
						});
						queryClient.invalidateQueries({
							queryKey: getGetEventsByShipmentNumberQueryKey(id),
						});
						queryClient.invalidateQueries({
							queryKey: getGetDocumentsOfShipmentQueryKey(id),
						});

						sonner.success(
							t(
								'pages.shipment-detail.form.submit.success.description',
								'Shipment updated successfully',
							),
						);
					}
				},
				onError: (error) => {
					const message = getApiErrorMessage(error);
					if (publishingRef.current) {
						sonner.error(
							t(
								'pages.shipment-detail.form.publish.error.description',
								'Publish failed. {{ errorMessage }}',
								{ errorMessage: message },
							),
						);
					} else {
						sonner.error(
							t(
								'pages.shipment-detail.form.update.error.description',
								'Update failed. {{ errorMessage }}',
								{ errorMessage: message },
							),
						);
					}
				},
			},
		});
	const { mutateAsync: addDocument } = useAddDocumentToShipment();

	const {
		refetch,
		data: invoiceLines,
		isError: isInvoiceLinesError,
		error: invoiceLinesError,
	} = useGetInvoiceLinesForPartyByShipment(id, {
		query: {
			enabled: false,
			select: (data) => data.data,
		},
	});

	const numberOfInvoicedLines =
		invoiceLines?.lines?.filter((line) => {
			return line.lineStatus === 'Invoiced';
		})?.length ?? 0;

	const setPublishStatus = useUpdateStatus({
		mutation: {
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey: shipmentQueryKey });
				queryClient.invalidateQueries({
					queryKey: getGetEventsByShipmentNumberQueryKey(id),
				});
				sonner.success(
					t(
						'pages.shipment-detail.api-success.update-status',
						'Shipment published successfully',
					),
				);
			},
			onError: (error) => {
				const message = getApiErrorMessage(error);
				if (message === 'CREDIT_LIMIT_EXCEEDED') {
					setCreditLimitPublishBlockedModalOpen(true);
					sonner.error(
						t(
							'pages.publish-shipment.api-error.credit-limit-exceeded',
							'Credit limit exceeded',
						),
					);
				} else {
					sonner.error(
						t(
							'pages.shipment-detail.api-error.update-status',
							'Could not publish shipment. {{errorMessage}}',
							{ errorMessage: message },
						),
					);
				}
			},
		},
	});

	const formRef = useRef<HTMLFormElement>(null);

	const watchPrice = form.watch('price.quantity');
	const watchPriceCurrencyCode = form.watch('price.currencyCode');
	const watchLocations = form.watch('locations');
	const watchIsNotPublishing = form.watch('concept');

	useEffect(() => {
		/**
		 * For now we just calculate the top position of the sticky bar once
		 * In the future if we want to support the height of the nav changing this will need to be updated
		 */
		if (stickyRef.current) {
			stickyRef.current.style.top = `${stickyRef.current
				.getBoundingClientRect()
				.top.toString()}px`;
		}
	}, [stickyRef]);

	useEffect(() => {
		if (organization?.settings) {
			// Pre-requisite for PublishDetailsSection.invoiceRequired which is loaded from the form.
			form.setValue(
				'invoiceRequired',
				organization.settings.invoiceRequired ?? false,
			);

			setOrgDefaultValues({
				invoiceRequired: organization.settings.invoiceRequired ?? false,
				conditions: organization.settings.shipmentCondition
					? ConditionType[organization.settings.shipmentCondition]
					: undefined,
			});
		}
		if (organization?.financialDetails) {
			// Pre-requisite for Financials component
			setCurrencyCode(organization.financialDetails.currency);
		}
	}, [organization]);

	useEffect(() => {
		if (!shipmentTsoStatus) {
			return;
		}

		if (shipmentTsoStatus === OrderStatus.INVOICED) {
			refetch();
		}
	}, [shipmentTsoStatus]);

	useEffect(() => {
		if (!shipmentStatus || !shipmentTsoStatus) {
			return;
		}

		const base = getReadOnlyFieldsList(
			shipmentStatus,
			shipmentTsoStatus,
			numberOfInvoicedLines,
		);

		if (shipmentStatus === ShipmentStatus.QUOTED) {
			setReadOnlyFields(base.filter((k) => !isQuoteEditablePath(k)));
		} else {
			setReadOnlyFields(base);
		}
	}, [shipmentStatus, shipmentTsoStatus, numberOfInvoicedLines]);

	// useEffect(() => {
	// 	setReadOnlyFields(
	// 		getReadOnlyFieldsList(
	// 			shipmentStatus!,
	// 			shipmentTsoStatus!,
	// 			numberOfInvoicedLines,
	// 		),
	// 	);
	// }, [shipmentStatus, numberOfInvoicedLines]);

	/**
	 * // TODO: Revisit the setIsCrossBorderShipment logic because and FR - UK shipment
	 *          is also cross-border, right?
	 *
	 * Currently we check both the 2 letter notation aswell as the 3 letter notation
	 * The reason for this is that the shipment endpoint which loads the initial data returns
	 * the 2 letter notation, but the autocomplete returns the 3 letter notation
	 *
	 * This counts for both the hasCrossBorderCountry and isGBRShipment check
	 *
	 */
	const setApplicableConditions = (values: Partial<FormValues>) => {
		const { countryCodes } = fromZodLocations(values.locations);

		setIsCrossBorderShipment(
			countryCodes.length > 0 &&
				countryCodes.some((country) => country !== 'NLD' && country !== 'NL'),
		);
		setIsGBRShipment(
			countryCodes.length > 0 &&
				countryCodes.every((country) => country === 'GBR' || country === 'GB'),
		);
	};

	const setInitValues = (data: ShipmentDto) => {
		const values = fromApi(data);

		// Merge against the clean default values, not the current form values.
		// Navigating between shipments (Prev/Next) reuses this component and its
		// form instance, so `form.getValues()` still holds the previous shipment's
		// data. Fields omitted by `fromApi` (e.g. `unCode` for non-ADR shipments)
		// would otherwise carry over and get saved onto the wrong shipment.
		const fullValues = { ...defaultFormValues, ...values };
		form.reset(fullValues);

		setWasConcept(data.shipmentStatus === ShipmentStatus.CONCEPT);
		setInitData({ ...values });

		setPendingChangeRequestActive(data.pendingChangeRequest ?? false);
		if (data.pendingChangeRequest) {
			refetchPendingChangeSet();
		}

		setAssignedCarrier(data.carrier);
		if (!data.carrier) {
			handleCarrierReviewUpdate();
		}

		setOldLocationActions(data.locationActions ?? []);
		// setNewLocationActions(data.locationActions ?? []);

		setShipmentTsoStatus(data.tsoStatus);
		if (data.tsoStatus) {
			setPendingExtraCost(data.tsoStatus === OrderStatus.COST_DECLARED);
		} else {
			setPendingExtraCost(false);
		}

		setShipmentStatus(data.shipmentStatus);
		setQuotes(data.quotes ?? []);

		setApplicableConditions(values);
	};

	const uploadDocument = async (
		file: File,
		fileRequest: Partial<FileRequest>,
	) => {
		const fileUploadType = shipmentMetaData?.fileTypes?.find(
			(type) => type.code && type.code === fileRequest.fileType,
		);

		if (!fileUploadType) {
			sonner.error(
				t(
					'pages.shipment-detail.modal.add-document.submit.error.description',
					'Upload failed. {{errorMessage}}',
					{ errorMessage: 'File type not found' },
				),
			);
			throw new Error('File type not found');
		}

		try {
			await addDocument({
				shipmentNumber: id,
				data: {
					file,
					properties: {
						description: fileRequest.description ?? '',
						fileName: fileRequest.fileName,
						type: fileUploadType,
					},
				},
			});

			queryClient.invalidateQueries({ queryKey: documentQueryKey });
			sonner.success(
				t(
					'pages.shipment-detail.modal.add-document.submit.success.description',
					'Document uploaded successfully',
				),
			);
		} catch (error) {
			const message = getApiErrorMessage(error);
			sonner.error(
				t(
					'pages.shipment-detail.modal.add-document.submit.error.description',
					'Upload failed. {{errorMessage}}',
					{ errorMessage: message },
				),
			);
			throw error;
		}
	};

	const handleFormSubmit = () => {
		if (formRef.current === null) {
			return;
		}

		form.clearErrors();

		formRef.current.dispatchEvent(
			new Event('submit', { cancelable: true, bubbles: true }),
		);
	};

	const handlePublish = () => {
		if (!userHasPermissions(Permission.PUBLISH_SHIPMENT)) {
			sonner.error(
				t(
					'pages.shipment-detail.sticky-bar-actions-denied.publish_shipment_button.title',
					'Waiting for approval',
				),
				{
					description: t(
						'pages.shipment-detail.sticky-bar-actions-denied.publish_shipment_button.description',
						'Cannot publish your shipment while your organisation is pending approval by UTURN.',
					),
				},
			);
			return;
		}
		form.setValue('concept', false, shouldUpdate);
		handleFormSubmit();
	};

	const isFormValid = async (data: FormValues, publishing: boolean) => {
		form.clearErrors();

		let results = validateGrossWeight(
			data.grossWeight?.quantity ?? 0,
			data.containerStatus ?? ContainerStatus.FULL,
			publishing,
		);

		results = validateLocationDates(
			data.locationActions,
			data.publishUntilDate ?? '',
			publishing,
			(isConcept && !publishing) ||
				isFieldReadOnly(readOnlyFields, 'publishUntilDate'),
			results,
		);

		const priceEditable = !isFieldReadOnly(readOnlyFields, 'price.quantity');

		results = validatePrice(
			data.price?.quantity ?? 0,
			publishing || (!isConcept && priceEditable),
			results,
		);

		results = validateShipperReference(
			data.shipperReference ?? '',
			data.invoiceRequired ?? false,
			publishing,
			results,
		);

		results = validateContainerNumber(
			data.equipment?.containerNumber ?? '',
			data.shippingLine?.code,
			results,
		);

		if (!results.valid) {
			results.issues.forEach(({ path, message }) =>
				form.setError(path, { type: 'custom', message }),
			);
			form.setFocus(results.issues[0].path, { shouldSelect: true });
			scrollToFirstFormError();

			return false;
		}

		// Non-blocking: check for duplicate container number
		// (shows warning but allows submission)
		results = await validateContainerNumberDuplicate(
			data.equipment?.containerNumber ?? '',
			Number(id),
		);

		if (!results.valid) {
			results.issues.forEach(({ message }) => {
				sonner.warning(message);
			});
		}

		return true;
	};

	const sendData = (data: UpdateShipmentV2, publishing: boolean) => {
		if (updatingShipment) {
			return;
		}

		publishingRef.current = publishing;
		updateShipment({ id, data });
	};

	const onSubmit = async (data: FormValues) => {
		/**
		 * Publishing or updating published shipment
		 * NOTE: see fromApi for concept initialization.
		 */
		const publishing = wasConcept && !(data.concept ?? true);

		const formValid = await isFormValid(data, publishing);
		if (!formValid) {
			return;
		}

		const putData = fromForm(data, id) as UpdateShipmentV2;

		// if QUOTED and quote-impact fields changed => confirm before proceeding
		if (
			shipmentStatus === ShipmentStatus.QUOTED &&
			hasQuoteImpactChanges(form.formState.dirtyFields)
		) {
			setPendingUpdateDeletingQuotesToConfirm({ data: putData, publishing });
			return;
		}

		if (isShipmentMatched(shipmentStatus)) {
			const { locationActions: newLocationActions } = fromZodLocations(
				data.locations,
			);
			const changesRequests = determineChangeRequest({
				oldValues: fromForm(initData, id) as UpdateShipmentV2,
				newValues: putData,
				oldLocationActions: structuredClone(oldLocationActions),
				newLocationActions: structuredClone(newLocationActions),
				dirtyFieldRefs: flattenObject(form.formState.dirtyFields),
			});
			if (changesRequests.length > 0 && promptChangeRequestModal.current) {
				setChangeRequestLocations({
					currentLocations: oldLocationActions,
					newLocations: newLocationActions,
				});
				setChangeRequestChanges(changesRequests);
				setOpenChangeRequestModal(true);
			} else {
				promptChangeRequestModal.current = true;
				const { publishUntilDate, ...withoutPublishUntil } = putData;
				sendData(withoutPublishUntil, publishing);
			}
		} else {
			sendData(putData, publishing);
		}
	};

	const handleQuotedShipmentUpdate = async () => {
		if (pendingUpdateDeletingQuotesToConfirm) {
			const { data: putData, publishing } =
				pendingUpdateDeletingQuotesToConfirm;
			await sendData(putData, publishing);
			setPendingUpdateDeletingQuotesToConfirm(null);
		}
	};

	const handleCancelQuotedShipmentUpdate = () => {
		form.reset(initData, { keepDefaultValues: true });
		setPendingUpdateDeletingQuotesToConfirm(null);
	};

	const handleShipmentNavigation = (shipmentNumber: number | undefined) => {
		if (!shipmentNumber) {
			return;
		}

		router.navigate({
			to: '/shipments/$shipmentNumber',
			params: {
				shipmentNumber: shipmentNumber.toString(),
			},
			state: {
				parentQueryKey: parentQueryKey,
				parentLocation: parentLocation,
			},
		});
	};

	/**
	 * Handles the logic of copying a shipment.
	 *
	 * @logic
	 * For a completed shipment, it doesn't make sense to copy over the container
	 * details and past shipment dates, since they can't be re-used for a new shipment.
	 *
	 * Container details to filter out are:
	 * - Container number
	 * - Tare weight
	 * - Cargo Gross weight
	 * - Seal
	 *
	 * For completed or cancelled shipments:
	 * - Any dates < current time
	 *
	 * Filter out carrier execution details, i.e.:
	 * - arrival
	 * - departure
	 * - eta
	 *
	 * Filter out BE-only managed shipping requirement codes, i.e.:
	 * - ADR
	 * - GENSET
	 *
	 * Others ignored fields during copy:
	 * - Preferred carriers
	 *
	 */
	const handleCopyShipment = (
		typeOfShipment: TypeOfShipment = TypeOfShipment.SINGLE,
	) => {
		const options: CopyOptions = {
			shipmentRequirements: shipmentMetaData?.shipmentRequirements ?? [],
			shippingRequirementExceptions,
		};

		const data = form.getValues();
		const { preferredCarrierIds, newGroup, ...toCopyData } = data;

		const copiedLocations = copyLocations(data);

		setCopyData({
			...toCopyData,
			typeOfShipment,
			equipment: { ...data?.equipment, containerNumber: '' },
			tareWeight: { ...data?.tareWeight, quantity: 0 },
			grossWeight: { ...data?.grossWeight, quantity: grossWeightUndefined },
			seal: '',
			locations: copiedLocations,
			publishUntilDate: getPickupDateUntilFromCopiedLocations(copiedLocations),
			requirementIds: copyRequirements(data, options),
			numberOfCopiesToBeCreated: 1,
		});

		router.navigate({ to: '/shipments/add/manual' });
	};

	/**
	 * Handles the logic of re-publishing a shipment.
	 *
	 * @logic
	 * When a shipment is cancelled, it is an exception. Meaning that
	 * the transport wasn't executed. So it could be, that the same
	 * shipment still needs to be transported and therefore, you want
	 * to keep the execution details.
	 *
	 * toRepublish is based on toCopy, except it keeps the container
	 * details instead of filtering them out.
	 */
	const handleRepublishShipment = (
		typeOfShipment: TypeOfShipment = TypeOfShipment.SINGLE,
	) => {
		const options: CopyOptions = {
			shipmentRequirements: shipmentMetaData?.shipmentRequirements ?? [],
			shippingRequirementExceptions,
		};

		const data = form.getValues();
		const { preferredCarrierIds, newGroup, ...toRepublishData } = data;

		const copiedLocations = copyLocations(data);

		setCopyData({
			...toRepublishData,
			typeOfShipment,
			locations: copiedLocations,
			publishUntilDate: getPickupDateUntilFromCopiedLocations(copiedLocations),
			requirementIds: copyRequirements(data, options),
			numberOfCopiesToBeCreated: 1,
		});

		router.navigate({ to: '/shipments/add/manual' });
	};

	useEffect(() => {
		const subscription = form.watch((value, { name, type }) => {
			if (name?.match(/^locations\.[0-9]+\.countryCode/)) {
				setApplicableConditions(value);
			}
		});

		return () => subscription.unsubscribe();
	}, [form.watch]);

	useEffect(() => {
		// const { conditions: defaultConditions } = orgDefaultValues ?? {};
		switch (true) {
			// case defaultConditions:
			// 	form.setValue('conditions', defaultConditions, shouldUpdate);
			// 	break;
			case isGBRShipment:
				form.setValue('conditions', ConditionType.RHA, shouldUpdate);
				break;
			case isCrossBorderShipment:
				form.setValue('conditions', ConditionType.CMR, shouldUpdate);
				break;
			default:
				form.setValue('conditions', ConditionType.AVC, shouldUpdate);
				break;
		}
	}, [isCrossBorderShipment, isGBRShipment, orgDefaultValues]);

	useEffect(() => {
		if (id && parentQueryKey) {
			setEnableShipmentNavigation(true);
			const navigation = getShipmentsNavigation({
				queryKey: parentQueryKey,
				currentShipmentNumber: id,
			});
			nextShipment.current = navigation.next;
			prevShipment.current = navigation.prev;
		}
	}, [id, parentQueryKey]);

	useEffect(() => {
		if (shipment?.data) {
			setInitValues(shipment.data);
		}
	}, [shipment?.data]);

	useEffect(() => {
		if (shipmentStatus) {
			const shipmentPrimaryActions: ButtonAction[] = [];
			switch (shipmentStatus) {
				case ShipmentStatus.QUOTED:
					shipmentPrimaryActions.unshift({
						label: t(
							'pages.shipment-detail.actions.quoted',
							'Quotes ({{quotes}})',
							{
								quotes: quotes?.length ?? 0,
							},
						),
						handleOnClick: () => {
							setOpenQuoteSheet(true);
						},
					});
					break;
				case ShipmentStatus.CANCELLED:
					shipmentPrimaryActions.unshift({
						label: t(
							'pages.shipment-detail.more.actions.republish',
							'Republish shipment',
						),
						handleOnClick: () => setOpenCopyTypeModal(true),
					});
					break;
				default:
					shipmentPrimaryActions.unshift({
						label: t(
							'pages.shipment-detail.more.actions.copy',
							'Copy shipment',
						),
						handleOnClick: () => setOpenCopyTypeModal(true),
					});
					break;
			}
			if (
				shipmentStatus === ShipmentStatus.PUBLISHED ||
				shipmentStatus === ShipmentStatus.QUOTED
			) {
				shipmentPrimaryActions.push({
					label: t(
						'pages.shipment-detail.more.actions.share',
						'Share shipment',
					),
					handleOnClick: () => {
						setOpenShareSheet(true);
					},
					icon: faArrowUpFromBracket,
					tooltip: t(
						'pages.shipment-detail.more.actions.share',
						'Share shipment',
					),
				});
			}
			const shipmentSecondaryActions: ButtonAction[] = [
				{
					label: t(
						'pages.shipment-detail.more.actions.print',
						'Print shipment',
					),
					handleOnClick: () => {
						window.print();
					},
				},
			];
			if (shipmentStatus === ShipmentStatus.QUOTED) {
				shipmentSecondaryActions.unshift({
					label: t('pages.shipment-detail.more.actions.copy', 'Copy shipment'),
					handleOnClick: () => setOpenCopyTypeModal(true),
				});
			}
			if (shipmentStatus === ShipmentStatus.CONCEPT) {
				shipmentSecondaryActions.unshift({
					label: t(
						'pages.shipment-detail.more.actions.archive',
						'Archive shipment',
					),
					handleOnClick: () => {
						setOpenCancelShipmentModal(true);
					},
				});
			}
			shipmentSecondaryActions.unshift({
				label: t('pages.shipment-detail.actions.events', 'Events'),
				handleOnClick: () => {
					setOpenEventSheet(true);
				},
			});
			setPrimaryActions(shipmentPrimaryActions);
			setSecondaryActions(shipmentSecondaryActions);
		}
	}, [shipmentStatus, watchLocations]);

	// TODO: Improve this deprecated onSuccess/onError workaround.
	useEffect(() => {
		if (isShipmentError) {
			sonner.error(
				t(
					'pages.shipment-detail.api-error.get-shipment',
					'Could not load shipment. {{errorMessage}}',
					{ errorMessage: getApiErrorMessage(shipmentError) },
				),
			);
		}
	}, [isShipmentError, shipmentError]);

	// TODO: Improve this deprecated onSuccess/onError workaround.
	useEffect(() => {
		if (isDocsError) {
			sonner.error(
				t(
					'pages.shipment-detail.api-error.get-shipment',
					'Could not load shipment. {{errorMessage}}',
					{ errorMessage: getApiErrorMessage(docsError) },
				),
			);
		}
	}, [isDocsError, docsError]);

	// TODO: Improve this deprecated onSuccess/onError workaround.
	useEffect(() => {
		if (isPendingChangeSetError) {
			sonner.error(
				t(
					'pages.shipment-detail.api-error.get-pending-change-set',
					'Could not load pending change requests. {{errorMessage}}',
					{ errorMessage: getApiErrorMessage(pendingChangeSetError) },
				),
			);
		}
		if (isPendingChangeSetSuccess) {
			const changeSetValues = fromApi(pendingChangeSet);
			form.reset(
				{
					...changeSetValues,
					requirementIds: Array.isArray(pendingChangeSet.requirements)
						? changeSetValues.requirementIds
						: (form.getValues('requirementIds') ?? []),
				},
				{ keepDefaultValues: true },
			);
		}
	}, [
		isPendingChangeSetError,
		pendingChangeSetError,
		isPendingChangeSetSuccess,
	]);

	// TODO: Improve this deprecated onSuccess/onError workaround.
	useEffect(() => {
		if (isReviewError) {
			handleCarrierReviewUpdate();
			sonner.error(
				t(
					'pages.shipment-detail.api-error.get-carrier-review',
					'Could not load carrier review. {{errorMessage}}',
					{ errorMessage: getApiErrorMessage(reviewError) },
				),
			);
		}
		if (isReviewSuccess) {
			handleCarrierReviewUpdate(review);
		}
	}, [isReviewError, reviewError, isReviewSuccess, review]);

	// TODO: Improve this deprecated onSuccess/onError workaround.
	useEffect(() => {
		if (isInvoiceLinesError) {
			sonner.error(
				t(
					'pages.shipment-detail.api-error.get-invoice-lines',
					'Could not load invoice lines for party. {{errorMessage}}',
					{ errorMessage: getApiErrorMessage(invoiceLinesError) },
				),
			);
		}
	}, [isInvoiceLinesError, invoiceLinesError]);

	if (isShipmentPending || isDocsPending) {
		return (
			<ShipmentDetailSkeletonLoader
				caption={t('general.loading', 'Loading...').toString()}
			/>
		);
	}

	return (
		<>
			<Helmet
				title={t('pages.shipments.details.pagetitle', '# {{ ID }}', {
					ID: id,
				}).toString()}
			/>
			<Page
				title={t('pages.shipments.details.title', 'Shipment ID - #{{ ID }}', {
					ID: id,
				}).toString()}
				backButton={{
					label: 'Shipments',
					href: parentLocation?.href ?? '/shipments',
					params: parentLocation?.params,
					search: parentLocation?.search,
				}}
			>
				{isConcept && <InsuredLimitThresholdBanner className="mt-4" />}
				<div
					className={cn(
						'relative grid min-h-screen grid-cols-[60%_auto] gap-2.5 pb-16 print:!flex print:flex-col',
						isConcept && 'pt-[4.4rem]',
					)}
				>
					<Form {...form}>
						<form
							onSubmit={form.handleSubmit(onSubmit, scrollToFirstFormError)}
							ref={formRef}
						>
							<div className="flex flex-col gap-4">
								{pendingChangeRequestActive && (
									<ChangeRequestBanner
										shipmentNumber={`${id}`}
										shipmentQueryKey={shipmentQueryKey}
										pendingChangeSetQueryKey={pendingChangeSetQueryKey}
									/>
								)}
								{pendingExtraCost && (
									<CostDeclaredBanner shipmentNumber={`${id}`} />
								)}
								{pendingCarrierReview && assignedCarrier && (
									<CarrierReviewBanner
										shipmentNumber={`${id}`}
										carrier={assignedCarrier}
										carrierReview={carrierReview}
									/>
								)}
								<ShipmentDetailsSection
									shipmentNumber={id}
									shipmentStatus={shipmentStatus}
									tsoStatus={shipmentTsoStatus}
									carrier={assignedCarrier}
									carrierReview={carrierReview}
									containerTypes={shipmentMetaData?.shipmentUnits ?? []}
									shipmentRequirements={
										shipmentMetaData?.shipmentRequirements ?? []
									}
									shippingLines={shipmentMetaData?.shippingLines ?? []}
									initDocuments={documents}
									uploadDocument={uploadDocument}
									documentQueryKey={documentQueryKey}
								/>
								<RouteSection
									shipmentNumber={id}
									shipmentStatus={shipmentStatus}
									transportType={initData?.transportType}
									isReadOnly={isFieldReadOnly(readOnlyFields, 'routeSection')}
								/>
								<PublishDetailsSection
									shipmentStatus={shipmentStatus}
									isCrossBorderShipment={isCrossBorderShipment}
									isGBRShipment={isGBRShipment}
								/>
							</div>
						</form>
						{import.meta.env.DEV && (
							<DevTool control={form.control} placement="top-right" />
						)}
						<div
							ref={stickyRef}
							className="sticky top-16 h-[50vh] print:hidden"
						>
							<div className="absolute -top-[4.4rem] right-0 flex gap-2">
								{enableShipmentNavigation && (
									<ShipmentsNavigationButtons
										handleShipmentNavigation={handleShipmentNavigation}
										prevShipmentNumber={prevShipment.current?.shipmentNumber}
										nextShipmentNumber={nextShipment.current?.shipmentNumber}
									/>
								)}
								{primaryActions?.map((action, index) => {
									if (action.icon) {
										return (
											<TooltipProvider key={`primary-action-${index}`}>
												<Tooltip>
													<TooltipTrigger asChild>
														<Button
															variant="outline"
															size="icon"
															aria-label={action.tooltip ?? action.label}
															onClick={() => action.handleOnClick()}
														>
															<Icon icon={action.icon} />
														</Button>
													</TooltipTrigger>
													{action.tooltip && (
														<TooltipContent>{action.tooltip}</TooltipContent>
													)}
												</Tooltip>
											</TooltipProvider>
										);
									}
									return (
										<Button
											key={`primary-action-${index}`}
											variant="outline"
											className="flex items-center gap-2"
											onClick={() => action.handleOnClick()}
										>
											{action.label}
										</Button>
									);
								})}
								{secondaryActions && (
									<DropdownMenu>
										<DropdownMenuTrigger asChild>
											<Button
												variant="outline"
												className="flex items-center gap-1"
											>
												<Icon icon={faEllipsisVertical} />
											</Button>
										</DropdownMenuTrigger>
										<DropdownMenuContent align="end">
											{secondaryActions?.map((action, index) => (
												<DropdownMenuItem
													key={`secondary-action-${index}`}
													onClick={() => action.handleOnClick()}
												>
													{action.label}
												</DropdownMenuItem>
											))}
										</DropdownMenuContent>
									</DropdownMenu>
								)}
							</div>
							<ShipmentMapSection
								shipmentStatus={shipmentStatus}
								initLocationActions={oldLocationActions}
							/>
							<SummarySection
								transportType={initData?.transportType}
								targetPrice={formatPrice(
									Math.round(watchPrice ?? 0),
									watchPriceCurrencyCode,
								)}
								totalPrice={formatPrice(
									shipment?.data?.priceIncludingShipmentFee?.quantity ?? 0,
									shipment?.data?.priceIncludingShipmentFee?.currencyCode ??
										'EUR',
								)}
								feePrice={formatPrice(
									shipment?.data?.shipmentFee?.quantity ?? 0,
									shipment?.data?.shipmentFee?.currencyCode ?? 'EUR',
								)}
								shipmentStatus={shipmentStatus ?? ShipmentStatus.UNKNOWN}
							>
								{!(
									shipmentTsoStatus === OrderStatus.CANCELLED ||
									(shipmentTsoStatus === undefined &&
										shipmentStatus === ShipmentStatus.CANCELLED &&
										numberOfInvoicedLines < 1)
								) && (
									<SideBarActions
										shipmentStatus={shipmentStatus ?? ShipmentStatus.UNKNOWN}
										isPublishing={!watchIsNotPublishing}
										isSubmitting={updatingShipment}
										onUpdate={handleFormSubmit}
										onPublish={handlePublish}
										onUnpublish={() => setOpenCancelShipmentModal(true)}
										onCancel={() => setOpenCancelShipmentModal(true)}
										isDirty={form.formState.isDirty}
										onOpenCreditLimitModal={() =>
											setCreditLimitPublishBlockedModalOpen(true)
										}
									/>
								)}
							</SummarySection>
						</div>
					</Form>
				</div>
				<>
					<EventsShipmentHistorySheet
						openSheet={openEventSheet}
						setOpenSheet={setOpenEventSheet}
						shipmentNumber={`${id}`}
					/>
					<QuotesSheet
						openSheet={openQuoteSheet}
						setOpenSheet={setOpenQuoteSheet}
						shipmentNumber={id}
					/>
					<ShareShipmentSheet
						openSheet={openShareSheet}
						setOpenSheet={setOpenShareSheet}
						shipmentNumber={id}
					/>
					<ChangeRequestModal
						openModal={openChangeRequestModal}
						setOpenModal={setOpenChangeRequestModal}
						changeRequestProps={{
							handleSubmit: () => {
								promptChangeRequestModal.current = false;
								handleFormSubmit();
							},
						}}
					/>
					{isShipmentCancellable(shipmentStatus) && (
						<CancelOrUnpublishShipmentModal
							shipmentNumber={id}
							shipmentStatus={shipmentStatus}
							shipmentQueryKey={shipmentQueryKey}
							eventsQueryKey={getGetEventsByShipmentNumberQueryKey(id)}
							openModal={openCancelShipmentModal}
							setOpenModal={setOpenCancelShipmentModal}
						/>
					)}
					<ConfirmQuotedShipmentUpdateModal
						open={pendingUpdateDeletingQuotesToConfirm !== null}
						onConfirm={handleQuotedShipmentUpdate}
						onCancel={handleCancelQuotedShipmentUpdate}
					/>
					<CopyShipmentTypeModal
						open={openCopyTypeModal}
						title={
							isRepublishFlow
								? t(
										'pages.shipment-detail.republish-type.title',
										'Republish shipment',
									)
								: undefined
						}
						description={
							isRepublishFlow
								? t(
										'pages.shipment-detail.republish-type.description',
										'Do you want to republish this as a single shipment or as a group of identical shipments?',
									)
								: undefined
						}
						onSelect={(typeOfShipment) => {
							setOpenCopyTypeModal(false);
							if (isRepublishFlow) {
								handleRepublishShipment(typeOfShipment);
							} else {
								handleCopyShipment(typeOfShipment);
							}
						}}
						onCancel={() => setOpenCopyTypeModal(false)}
					/>
				</>
				<CreditLimitPublishBlockedModal
					open={creditLimitPublishBlockedModalOpen}
					onSubmit={() => {
						form.setValue('concept', true, shouldUpdate);
						handleFormSubmit();
						setCreditLimitPublishBlockedModalOpen(false);
					}}
					onCancel={() => {
						setCreditLimitPublishBlockedModalOpen(false);
					}}
				/>
			</Page>
		</>
	);
}
